import api from "./apiService";

/**
 * Fetch songs accessible to the current user with various filtering options
 * @param {Object} options - Filter options
 * @param {boolean} options.communityOnly - Only fetch community songs (default: false)
 * @param {boolean} options.userOnly - Only fetch user songs (default: false)
 * @param {number} options.communityId - Filter by community ID
 * @param {number} options.collectionId - Filter by collection ID
 * @returns {Promise<Array>} List of songs
 */
export const fetchSongs = async (options = {}) => {
    try {
        // Build query parameters
        const params = new URLSearchParams();
        
        if (options.communityOnly !== undefined) {
            params.append('community_only', options.communityOnly);
        }
        
        if (options.userOnly !== undefined) {
            params.append('user_only', options.userOnly);
        }
        
        if (options.communityId) {
            params.append('community_id', options.communityId);
        }
        
        if (options.collectionId) {
            params.append('collection_id', options.collectionId);
        }
        
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await api.get(`/songs${queryString}`);
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch community-specific songs
 * @param {number} communityId - The community ID
 * @returns {Promise<Array>} List of songs
 */
export const fetchCommunitySongs = async (communityId) => {
    try {
        const response = await api.get(`/songs/community/${communityId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch a song by ID
 * @param {number} songId - The song ID
 * @returns {Promise<Object>} Song data
 */
export const fetchSongById = async (songId) => {
    try {
        const response = await api.get(`/songs/${songId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new song
 * @param {Object} songData - The song data
 * @param {string} songData.title - The title of the song
 * @param {string} songData.composer - The composer
 * @param {string} songData.lyrics - The lyrics
 * @param {string} songData.owner_type - The owner type ('user' or 'community')
 * @param {number} songData.owner_id - The owner ID (user ID or community ID)
 * @param {number} songData.community_id - Alternative to owner_type/owner_id for community songs
 * @returns {Promise<Object>} Created song data
 */
export const createSong = async (songData) => {
    try {
        const response = await api.post("/songs", songData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a song directly for a community (admin only)
 * @param {number} communityId - The community ID
 * @param {Object} songData - The song data
 * @returns {Promise<Object>} Created song data
 */
export const createCommunitySong = async (communityId, songData) => {
    try {
        const response = await api.post(`/songs/community/${communityId}`, songData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an existing song
 * @param {number} songId - The ID of the song to update
 * @param {Object} songData - The updated song data
 * @returns {Promise<Object>} Updated song data
 */
export const updateSong = async (songId, songData) => {
    try {
        const response = await api.put(`/songs/${songId}`, songData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a song
 * @param {number} songId - The ID of the song to delete
 * @returns {Promise<Object>} Response message
 */
export const deleteSong = async (songId) => {
    try {
        await api.delete(`/songs/${songId}`);
        return { message: "Song deleted successfully" };
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch songs that have part files
 * @param {Object} options - Filter options
 * @param {number} options.communityId - Filter by community ID
 * @returns {Promise<Array>} List of songs with part files
 */
export const fetchSongsWithParts = async (options = {}) => {
    try {
        let url = '/songs/with-parts';
        
        // Add query parameters if provided
        if (options.communityId) {
            url += `?community_id=${options.communityId}`;
        }
        
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Upload a part file for a song
 * @param {number} songId - The song ID
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Optional callback for upload progress
 * @returns {Promise<Object>} Updated song data
 */
export const uploadSongPart = async (songId, file, onProgress) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post(`/songs/${songId}/upload-part`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: onProgress ? 
                (progressEvent) => {
                    const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentage);
                } : undefined
        });
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Get the URL for downloading a song part file
 * @param {number} songId - The song ID
 * @param {boolean} forDownload - Whether the URL should trigger a download (optional)
 * @returns {string} URL for downloading the part file
 */
export const getSongPartUrl = (songId, forDownload = false) => {
    // Read the base URL from the environment variable (or default to an empty string)
    const envBase = process.env.REACT_APP_API_URL || '';
    // Ensure no double slash if the env variable ends with a slash
    const baseUrl = envBase.endsWith('/') ? envBase.slice(0, -1) : envBase;
    
    // Optionally, if your backend routes are behind an /api prefix, make sure to include it.
    // For instance, if you expect your backend URLs to be like http://localhost:5000/api/songs/...
    const downloadParam = forDownload ? '?download=true' : '';
    const url = `${baseUrl}/api/songs/${songId}/part-file${downloadParam}`;
    return url;
};


/**
 * Delete a song part file
 * @param {number} songId - The song ID
 * @returns {Promise<Object>} Response message
 */
export const deleteSongPart = async (songId) => {
    try {
        const response = await api.delete(`/songs/${songId}/part-file`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Copy a song to a different owner (user or community)
 * @param {number} songId - The source song ID
 * @param {string} targetOwnerType - 'user' or 'community'
 * @param {number} targetOwnerId - User ID or Community ID
 * @param {string} newTitle - Optional new title for the copied song
 * @returns {Promise<Object>} The newly created song
 */
/**
 * Fetch tags (collections used as tags) accessible to the current user
 * @param {string} ownerType - 'user' or 'community'
 * @param {number} ownerId - User ID or Community ID
 * @returns {Promise<Array>} List of tags [{id, name, owner_type, owner_id}]
 */
export const fetchTags = async (ownerType, ownerId) => {
    try {
        const params = new URLSearchParams();
        if (ownerType) params.append('owner_type', ownerType);
        if (ownerId) params.append('owner_id', ownerId);
        const response = await api.get(`/songs/tags?${params.toString()}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const copySong = async (songId, targetOwnerType, targetOwnerId, newTitle = null) => {
    try {
        const payload = {
            target_owner_type: targetOwnerType,
            target_owner_id: targetOwnerId
        };
        if (newTitle) {
            payload.new_title = newTitle;
        }
        const response = await api.post(`/songs/${songId}/copy`, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};
